import React, { useEffect, useMemo, useState } from 'react';
import { FORM_FREQUENCY_TABS } from '../config/safetyForms';

/** Status category tabs (Overdue → Pending → All). */
export const FORM_STATUS_TABS = [
  { id: 'overdue', label: 'Overdue forms' },
  { id: 'pending', label: 'Pending forms' },
  { id: 'all', label: 'All forms' },
];

const formSearchInputClass =
  'w-full px-2.5 py-1.5 rounded-md bg-black border border-gray-700 text-xs text-gray-200 placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-amber-300/40 focus:border-amber-300/40';

function matchesFormSearch(card, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [card.title, card.description, card.frequencyNote]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/**
 * Compact frequency tabs + form list for supervisor / worker left sidebars.
 * Filter buttons collapse under a single "Safety Forms" toggle to keep the list visible.
 */
export function SafetyFormFrequencyNav({
  cards,
  frequency,
  onFrequencyChange,
  activeFormId,
  activeFormIndex,
  onSelectForm,
  emptyText = 'No forms in this category.',
  showFormStatusTabs = false,
  formStatus = 'pending',
  onFormStatusChange,
  showFrequencyTabs = true,
  activeFilterKind = 'status',
  filtersAlwaysOpen = false,
}) {
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(filtersAlwaysOpen);
  const [overdueIgnored, setOverdueIgnored] = useState(false);
  const [confirmIgnoreOverdue, setConfirmIgnoreOverdue] = useState(false);
  const visibleCards = useMemo(
    () => (cards || []).filter((card) => matchesFormSearch(card, search)),
    [cards, search]
  );

  useEffect(() => {
    if (!confirmIgnoreOverdue) return undefined;
    const timer = window.setTimeout(() => setConfirmIgnoreOverdue(false), 3000);
    return () => window.clearTimeout(timer);
  }, [confirmIgnoreOverdue]);

  const tabButtonClass = (active) =>
    `w-full px-2.5 py-[7px] rounded-md text-sm font-semibold tracking-wide leading-none transition-colors border ${
      active
        ? 'bg-amber-400/15 border-amber-400/40 text-amber-100'
        : 'bg-gray-900/60 border-gray-800 text-gray-400 hover:text-gray-200 hover:border-gray-700'
    }`;

  const overdueButtonClass = () => {
    if (overdueIgnored) {
      return 'w-full px-2.5 py-[7px] rounded-md text-sm font-semibold tracking-wide leading-none transition-colors border bg-white border-white text-black hover:bg-gray-100';
    }
    return 'w-full px-2.5 py-[7px] rounded-md text-sm font-semibold tracking-wide leading-none transition-colors border bg-red-600 border-red-500 text-white hover:bg-red-500';
  };

  const handleOverdueClick = () => {
    onFormStatusChange?.('overdue');
    if (overdueIgnored) {
      setConfirmIgnoreOverdue(false);
      return;
    }
    if (confirmIgnoreOverdue) {
      setOverdueIgnored(true);
      setConfirmIgnoreOverdue(false);
      return;
    }
    setConfirmIgnoreOverdue(true);
  };

  const hasFilterPanel = showFormStatusTabs || showFrequencyTabs;
  const panelOpen = hasFilterPanel && (filtersAlwaysOpen || filtersOpen);

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {hasFilterPanel ? (
        <button
          type="button"
          onClick={() => {
            if (filtersAlwaysOpen) return;
            setFiltersOpen((open) => !open);
          }}
          aria-expanded={panelOpen}
          aria-disabled={filtersAlwaysOpen || undefined}
          className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-md text-sm font-medium transition-colors mb-1.5 shrink-0 border ${
            panelOpen
              ? 'bg-gray-800 border-gray-700 text-white'
              : 'bg-gray-900/60 border-gray-800 text-gray-200 hover:bg-gray-900 hover:text-white'
          } ${filtersAlwaysOpen ? 'cursor-default' : ''}`}
        >
          <span>Safety Forms</span>
          <span
            className={`shrink-0 text-base leading-none text-gray-300 transition-transform ${
              panelOpen ? 'rotate-90' : ''
            }`}
            aria-hidden="true"
          >
            ›
          </span>
        </button>
      ) : (
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1 mb-2">
          Safety Forms
        </p>
      )}

      {panelOpen ? (
        <div className="shrink-0 mb-2 space-y-2 rounded-md border border-gray-800 bg-gray-950/80 p-2">
          {showFormStatusTabs ? (
            <div
              className="grid grid-cols-1 gap-1.5 w-full"
              role="tablist"
              aria-label="Form status"
            >
              {FORM_STATUS_TABS.map((tab) => {
                const active = activeFilterKind === 'status' && formStatus === tab.id;
                if (tab.id === 'overdue') {
                  return (
                    <div key={tab.id} className="relative overflow-hidden rounded-md">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={handleOverdueClick}
                        className={`${overdueButtonClass()} ${confirmIgnoreOverdue ? 'invisible' : ''}`}
                      >
                        {tab.label}
                      </button>
                      {confirmIgnoreOverdue ? (
                        <button
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={handleOverdueClick}
                          className="absolute inset-0 flex items-center justify-center rounded-md bg-red-700 text-sm font-semibold text-red-50 hover:bg-red-600"
                          title="Click again to ignore overdue alert"
                          aria-label="Confirm ignore overdue forms"
                        >
                          Ignore?
                        </button>
                      ) : null}
                    </div>
                  );
                }
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => {
                      setConfirmIgnoreOverdue(false);
                      onFormStatusChange?.(tab.id);
                    }}
                    className={tabButtonClass(active)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          {showFrequencyTabs ? (
            <div
              className="grid grid-cols-2 gap-1.5 w-full"
              role="tablist"
              aria-label="Form frequency"
            >
              {FORM_FREQUENCY_TABS.map((tab) => {
                const active = activeFilterKind === 'frequency' && frequency === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => onFrequencyChange(tab.id)}
                    className={tabButtonClass(active)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mb-2 shrink-0">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search forms…"
          aria-label="Search safety forms"
          className={formSearchInputClass}
        />
      </div>

      <nav className="space-y-0.5 flex-1 overflow-y-auto min-h-0" role="tabpanel">
        {!visibleCards.length ? (
          <p className="text-xs text-gray-500 px-2 py-3">
            {search.trim() ? 'No forms match your search.' : emptyText}
          </p>
        ) : (
          visibleCards.map((card) => {
            const selected =
              activeFormId != null
                ? activeFormId === card.id
                : activeFormIndex != null && activeFormIndex === card.index;
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => onSelectForm(card)}
                className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  selected
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-400 hover:bg-gray-900 hover:text-white'
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block break-words">{card.title}</span>
                  {card.frequencyNote ? (
                    <span className="block text-[10px] text-gray-500 mt-0.5 font-normal">
                      {card.frequencyNote}
                    </span>
                  ) : null}
                </span>
                <span className="ml-1 shrink-0 text-base leading-none text-gray-300" aria-hidden="true">
                  ›
                </span>
              </button>
            );
          })
        )}
      </nav>
    </div>
  );
}
